import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getPlayerChips } from "../redux/playerInfoSlice";
import { fetchAdminStatus, fetchNotifications } from "../redux/notificationsSlice";
import { formatAmount } from "../utilities/chips";
import NotificationPanel from "./NotificationPanel";

// Small gold poker-chip glyph for the chip pill (crisp at any size, matches the
// gold theme without shipping an image asset).
const ChipIcon = () => (
    <svg
        className="chip-pill-icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="10" fill="#ffd75e" />
        <circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke="#fff2c4"
            strokeWidth="1.2"
            strokeDasharray="3 3"
            opacity="0.9"
        />
        <circle
            cx="12"
            cy="12"
            r="5.5"
            fill="none"
            stroke="#8a6a00"
            strokeWidth="1.4"
            opacity="0.5"
        />
        <circle cx="12" cy="12" r="3" fill="#ffe9a8" />
    </svg>
);

// Notification bell for the far-right of the top bar. Line-art so it stays crisp
// at any size and matches the muted username color without an image asset.
const BellIcon = () => (
    <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

const TopMenu = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { chips, username, loading, error } = useSelector(state => state.playerInfo);
    const unreadCount = useSelector(state => state.notifications.unreadCount);
    const [panelOpen, setPanelOpen] = useState(false);

    useEffect(() => {
        dispatch(getPlayerChips());
        // Admin-blast state: whether to show the hamburger's "Admin controls" entry, and the
        // bell's items + unread badge. TopMenu only renders authenticated, so both are safe;
        // the socket's onConnect refetch keeps the list healed after reconnects.
        dispatch(fetchAdminStatus());
        dispatch(fetchNotifications());
    }, [dispatch]);

    // The bell is scoped to the home screen per the request; the identity/chip
    // content stays centered because the bell is absolutely positioned.
    const showBell = location.pathname === "/home";

    return (
        <div className="top-menu">
          {loading ? (
            <span className="top-menu-status">Loading…</span>
          ) : error ? (
            <span className="top-menu-status top-menu-error">Error: {error}</span>
          ) : (
            <div className="top-menu-identity">
              <span className="top-menu-username">{username}</span>
              <span className="chip-pill">
                <ChipIcon />
                <span className="chip-pill-amount">{formatAmount(chips)}</span>
              </span>
            </div>
          )}
          {showBell && (
            <button
              type="button"
              className="top-menu-bell"
              aria-label="Notifications"
              onClick={() => setPanelOpen(o => !o)}
            >
              <BellIcon />
              {unreadCount > 0 && <span className="top-menu-bell-badge" aria-hidden="true" />}
            </button>
          )}
          {showBell && panelOpen && (
            <NotificationPanel onClose={() => setPanelOpen(false)} />
          )}
        </div>
      );


};

export default TopMenu;
